import React, { createContext, useCallback, useMemo } from 'react'
import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'
import { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import {
  type GetColumnItem,
  type GetCellData,
  type GetColumnData,
  cellDataFactory,
  columnDataFactory
} from '@aytm/app/insights/dashboard/helpers/values'
import { stateSelector, shouldUpdate, type StateSelectorParams } from './selectors'

export type ValueContextType = {
  getColumnItem: GetColumnItem
  getCellData: GetCellData
  getColumnData: GetColumnData
}

export const ValueContext = createContext<ValueContextType | undefined>(undefined)

export const ValueContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    items,
    data,
    stat,
    subjects,
    attributes,
    sortByAttribute,
    showSigtest,
    showImages,
    currentMethod,
    currentSplit,
    colors
  } = usePool<typeof InsightsDashboardPool, StateSelectorParams, InsightsDashboardPoolState>(
    InsightsDashboardPool,
    stateSelector,
    shouldUpdate
  )

  const getColumnItem = useCallback((uid: string) => items?.toJS()?.find(v => v.uid === uid) || null, [items])

  const getCellData = useMemo<GetCellData>(
    () =>
      cellDataFactory({
        items,
        data,
        stat,
        subjects,
        attributes,
        sortByAttribute,
        showSigtest,
        colors,
        currentSplit
      }),
    [items, data, stat, subjects, attributes, sortByAttribute, showSigtest, colors, currentSplit]
  )

  const getColumnData = useMemo<GetColumnData>(
    () =>
      columnDataFactory({
        items,
        stat,
        subjects,
        attributes,
        sortByAttribute,
        showSigtest,
        showImages,
        currentMethod
      }),
    [attributes, currentMethod, stat, items, showImages, showSigtest, sortByAttribute, subjects]
  )

  const contextValue = useMemo(() => {
    return { getColumnItem, getCellData, getColumnData }
  }, [getColumnItem, getCellData, getColumnData])

  return <ValueContext.Provider value={contextValue}>{children}</ValueContext.Provider>
}
